import React, { useEffect, useState } from 'react';
import { Badge } from "react-bootstrap";
import SearchableUserDropdown from './SearchableUserDropdown';
import { ListPlaceholder } from "../../Components/ui/listPlaceholder/listPlaceholder";
import styles from './KeyDate.module.scss';
import AddInfileTaskModal from './AddInfileTaskModal';
import { Button } from 'react-bootstrap';
import { runInnerFunction } from '../../compatManager';
import { setInfileTasks } from '../../redux/reducer';
import { useDispatch } from 'react-redux';
import { AddTaskPrefill } from '../../fileToolbar/fileToolbarTypes';

interface TasklistTabProps {
    SESSION_INFO: any;
    filterByUser: string[];
    setFilterByUser: (value: string[]) => void;
    allLawyers: any[];
    myTasks: any[];
    tasks: any[];
    setTasks: (tasks: any[]) => void;
    renderTaskItem: (task: any, index: any) => JSX.Element;
    isSameDay: (date1: Date, date2: Date) => boolean;
    formatDateHeader: (date: Date) => string;
    getTaskStatus: (dateString: string) => { label: string, color: string };
    addTaskRequest?: { id: number; prefill: AddTaskPrefill } | null;
}

const TasklistTab: React.FC<TasklistTabProps> = ({
    SESSION_INFO,
    filterByUser,
    setFilterByUser,
    allLawyers,
    myTasks,
    tasks,
    setTasks,
    renderTaskItem,
    isSameDay,
    formatDateHeader,
    getTaskStatus,
    addTaskRequest
}) => {
    // State for the Add Task modal
    const [showAddModal, setShowAddModal] = useState(false);
    const [modalPrefill, setModalPrefill] = useState<AddTaskPrefill | null>(null);
    const dispatch = useDispatch();

    const resolveCurrentUserAssigneeId = (): string | null => {
        const sessionUserId = SESSION_INFO?.user?.id;
        if (sessionUserId === undefined || sessionUserId === null) return null;
        const sessionIdStr = String(sessionUserId);
        const match = allLawyers?.find((u) =>
            String(u?.SubUserId ?? "") === sessionIdStr || String(u?.UserID ?? "") === sessionIdStr
        );
        const resolved = match?.SubUserId ?? match?.UserID ?? sessionUserId;
        return resolved !== undefined && resolved !== null ? String(resolved) : null;
    };
    
    // Set default user to current user on initial load
    useEffect(() => {
        if (filterByUser.length === 0) {
            setFilterByUser([SESSION_INFO.user.id.toString()]);
        }
    }, []);

    useEffect(() => {
        if (!addTaskRequest) return;
        const currentAssigneeId = resolveCurrentUserAssigneeId();
        setModalPrefill({
            ...addTaskRequest.prefill,
            assignedUsers:
                addTaskRequest.prefill.assignedUsers ??
                (currentAssigneeId ? [currentAssigneeId] : undefined),
        });
        setShowAddModal(true);
    }, [addTaskRequest?.id]);

    // Handler for the SearchableUserDropdown
    const handleUserSelect = (userId: any) => {
        const newUserFilter = userId === 0 || userId === "all" ? [] : 
                             userId === "nouser" ? ["nouser"] : [userId.toString()];
        setFilterByUser(newUserFilter);
    };

    // Function to add a new task to tasks state
    const handleAddTask = (newTask: any) => {
        // Add the new task to tasks state in parent component
        let updatedTasks = [...tasks];

        updatedTasks = [
            ...updatedTasks,
            newTask
        ];
        
        setTasks(updatedTasks);
        // Trigger page dirty state
        const tasksCopy = updatedTasks.map(tsk => ({ ...tsk }));
        dispatch(setInfileTasks(tasksCopy));
        runInnerFunction("SetPageDirty", null);
    };

    // Helper function to check if a user exists in allLawyers
    const findUserById = (userId: string): any | undefined => {
        if (!userId) return undefined;
        
        const user = allLawyers.find(user => 
            user.SubUserId?.toString() === userId || 
            user.UserID?.toString() === userId ||
            user.LSUCID === userId
        );
        
        return user || null;
    };

    // Filter tasks - only regular tasks (not key dates) and filter by user if selected
    const filteredTasks = myTasks.filter(task => {
        // Skip tasks without required data
        if (!task) return false;
        
        // Display filter - only show tasks that are active and not overwritten
        if (task.isActivated !== 1) {
            return false;
        }
        
        // If a user filter is applied
        if (filterByUser.length > 0) {
            // Handle "nouser" filter - show tasks with no assigned users OR with deleted users
            if (filterByUser[0] === "nouser") {
                const responsibleUserString = task.userResponsible;
                
                // If no userResponsible string, show the task
                if (!responsibleUserString || responsibleUserString.trim() === '') {
                    return true;
                }
                
                // Check if all assigned users are deleted/non-existent
                const responsibleUsers = responsibleUserString.split(',').map(id => id.trim());
                const allUsersDeleted = responsibleUsers.every(userId => {
                    const foundUser = findUserById(userId);
                    return foundUser === null; // User doesn't exist (deleted)
                });
                
                return allUsersDeleted;
            }
            
            // Normal user filtering
            const responsibleUserString = task.userResponsible;
            
            if (!responsibleUserString) return false;
            
            const responsibleUsers = responsibleUserString.split(',').map(id => id.trim());
            return responsibleUsers.includes(filterByUser[0]);
        }
        
        return true;
    });
    
    // Group tasks by date
    const tasksByDate = new Map<string, {
        date: Date,
        formattedDate: string,
        isToday: boolean,
        tasks: Array<any>
    }>();
    
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    
    // Process tasks and group by date
    filteredTasks.forEach(task => {
        const dateToUse = task.date;
        
        let taskDate: Date;
        
        // Ensure proper date parsing
        const dateParts = dateToUse.split('-');
        if (dateParts.length === 3) {
            // Parse as YYYY-MM-DD
            const year = parseInt(dateParts[0], 10);
            const month = parseInt(dateParts[1], 10) - 1; // Months are 0-indexed in JS
            const day = parseInt(dateParts[2], 10);
            
            taskDate = new Date(year, month, day);
        } else {
            // Try standard JS date parsing as fallback
            taskDate = new Date(dateToUse);
        }
        
        // Skip invalid dates
        if (isNaN(taskDate.getTime())) return;
        
        // Normalize date for grouping (remove time component)
        taskDate.setHours(0, 0, 0, 0);
        const dateKey = taskDate.toISOString().split('T')[0];
        const isToday = isSameDay(taskDate, currentDate);
        
        if (!tasksByDate.has(dateKey)) {
            tasksByDate.set(dateKey, {
                date: taskDate,
                formattedDate: formatDateHeader(taskDate),
                isToday,
                tasks: []
            });
        }
        
        tasksByDate.get(dateKey).tasks.push(task);
    });
    
    // Check if today exists in the tasks
    const todayISOString = currentDate.toISOString().split('T')[0];
    const hasTodayGroup = tasksByDate.has(todayISOString);
    
    // If today doesn't exist in the tasks, add it
    if (!hasTodayGroup) {
        tasksByDate.set(todayISOString, {
            date: currentDate,
            formattedDate: formatDateHeader(currentDate),
            isToday: true,
            tasks: []
        });
    }
    
    // Convert map to array and sort by date
    const groupedTasks = Array.from(tasksByDate.values())
        .sort((a, b) => a.date.getTime() - b.date.getTime());
    
    return (
        <>
            {/* User filter dropdown with Add Task button */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <SearchableUserDropdown
                    users={allLawyers}
                    selectedUserId={filterByUser.length > 0 ? filterByUser[0] : "all"}
                    currentUserId={SESSION_INFO.user?.id}
                    onUserSelect={handleUserSelect}
                    showNotAssigned={true}
                    dropdownAlign="end"
                />
                
                <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={() => setShowAddModal(true)}
                >
                    <i className="fa-solid fa-plus me-1"></i> Task
                </Button>
            </div>

            {/* Display filtered tasks */}
            {filteredTasks.length === 0 && !hasTodayGroup ? (
                <ListPlaceholder 
                    icon="fa-light fa-message-check" 
                    title="All tasks up to date."
                    msg="There are no tasks currently assigned to this user."
                />
            ) : (
                groupedTasks.map((group, groupIndex) => (
                    <div key={`task-date-group-${groupIndex}`} className={styles.date}>
                        {/* Date header with Today badge if applicable */}
                        {group.isToday ? (
                            <div style={{marginBottom:'0', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                <h4>{group.formattedDate}</h4>
                                <Badge pill bg="warning-heavy">Today</Badge>
                            </div>
                        ) : (
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                <h5>{group.formattedDate}</h5>
                                {(() => {
                                    const status = getTaskStatus(group.date.toISOString().split('T')[0]);
                                    return (
                                        <Badge pill bg={status.color} style={{fontSize: '0.75rem'}}>
                                            {status.label}
                                        </Badge>
                                    );
                                })()}
                            </div>
                        )}

                        {/* Tasks for this date */}
                        {group.tasks.length === 0 ? (
                            <p className="text-muted small">No tasks for this date.</p>
                        ) : (
                            group.tasks.map((task, taskIndex) => (
                                <div key={`task-${groupIndex}-${taskIndex}`} className="options-container">
                                    {renderTaskItem(task, taskIndex)}
                                </div>
                            ))
                        )}
                    </div>
                ))
            )}

            {/* Add Task Modal */}
            <AddInfileTaskModal
                show={showAddModal}
                setShow={(show) => {
                    if (!show) setModalPrefill(null);
                    setShowAddModal(show);
                }}
                allLawyers={allLawyers}
                myTasks={myTasks}
                tasks={tasks}
                SESSION_INFO={SESSION_INFO}
                onAddTask={handleAddTask}
                prefill={modalPrefill}
            />
        </>
    );
};

export default TasklistTab; 